import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const emptyForm = {
  id: '', name: '', payment_date: '', bank_transaction_id: '',
  bank_transaction_date: '', payment_amount: '', balance_amount: '',
  reconciled: '', payment_type_id: '', comments: '',
};

export default function PaymentsPanel({ onClose }) {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [showPaymentType, setShowPaymentType] = useState(false);
  const [disableReconciled, setDisableReconciled] = useState(false);

  const [payments, setPayments] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [searchFilters, setSearchFilters] = useState({ name: '', payment_date: '', reconciled: 'N' });

  const [linesOpen, setLinesOpen] = useState(false);
  const [lines, setLines] = useState([]);
  const [linesTitle, setLinesTitle] = useState('');

  useEffect(() => {
    fetchPayments({ name: '', payment_date: '', reconciled: 'N' });
    fetchPaymentTypes();
  }, []);

  const fetchPayments = async (filters) => {
    try {
      const res = await axios.get(`${API_URL}/payments`, {
        params: filters !== undefined ? filters : searchFilters,
        ...auth(),
      });
      setPayments(res.data);
    } catch (e) {
      console.error('Error fetching payments:', e);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/commonLOVs/payment-types`, {
        params: { from_to: 'T' },
        ...auth(),
      });
      setPaymentTypes(res.data);
    } catch (e) {
      console.error('Error fetching payment types:', e);
    }
  };

  const fetchLines = async (id, name) => {
    try {
      const res = await axios.get(`${API_URL}/payments/lines/${id}`, auth());
      setLines(res.data);
      setLinesTitle(name);
      setLinesOpen(true);
    } catch (e) {
      console.error('Error fetching payment lines:', e);
    }
  };

  const handleSearchChange = (e) => {
    const updated = { ...searchFilters, [e.target.name]: e.target.value };
    setSearchFilters(updated);
    fetchPayments(updated);
  };

  const handleReset = () => {
    const reset = { name: '', payment_date: '', reconciled: '' };
    setSearchFilters(reset);
    fetchPayments(reset);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'reconciled') {
      setShowPaymentType(form.reconciled === 'N' && value === 'Y');
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (p) => {
    setForm({ ...p, payment_date: fmtDate(p.payment_date) });
    setShowPaymentType(false);
    setDisableReconciled(p.reconciled === 'Y');
    setEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/payments/${form.id}`, form, auth());
      setForm(emptyForm);
      setEditing(false);
      setShowPaymentType(false);
      setDisableReconciled(false);
      fetchPayments();
    } catch (e) {
      console.error('Error saving payment:', e);
    }
  };

  return (
    <>
      <div className="bt-fm-header">
        <span className="bt-fm-title">Payments</span>
        <button className="bt-fm-close" onClick={onClose}>×</button>
      </div>

      <div className="bt-fm-body">
        {/* Edit form */}
        <h3 className="bt-panel-h">Edit Payment</h3>
        <form onSubmit={handleSubmit}>
          <div className="bt-form-row">
            <input
              type="text" name="name" value={form.name}
              placeholder="Payment Name" readOnly className="bt-input"
            />
            <input
              type="date" name="payment_date" value={form.payment_date}
              readOnly className="bt-input"
            />
            <input
              type="text" name="payment_amount" value={form.payment_amount}
              placeholder="Amount" readOnly className="bt-input bt-input-sm"
            />
            <input
              type="text" name="comments" value={form.comments || ''}
              placeholder="Comments" readOnly={!editing}
              onChange={handleChange} className="bt-input"
            />
            <select
              name="reconciled" value={form.reconciled}
              onChange={handleChange}
              disabled={!editing || disableReconciled}
              className="bt-select"
            >
              <option value="">Select</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
            {showPaymentType && (
              <select
                name="payment_type_id" value={form.payment_type_id || ''}
                onChange={handleChange} required className="bt-select"
              >
                <option value="">Select Payment Type</option>
                {paymentTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
          <button type="submit" className="bt-btn-primary bt-mt-8" disabled={!editing}>
            Update Payment
          </button>
        </form>

        {/* Search */}
        <h3 className="bt-panel-h bt-mt-20">Payments List</h3>
        <div className="bt-form-row bt-mb-12">
          <input
            type="text" name="name" value={searchFilters.name}
            placeholder="Name" onChange={handleSearchChange} className="bt-input"
          />
          <input
            type="date" name="payment_date" value={searchFilters.payment_date}
            onChange={handleSearchChange} className="bt-input"
          />
          <select
            name="reconciled" value={searchFilters.reconciled}
            onChange={handleSearchChange} className="bt-select"
          >
            <option value="">All</option>
            <option value="Y">Yes</option>
            <option value="N">No</option>
          </select>
          <button className="bt-btn-secondary" onClick={handleReset}>Reset</button>
        </div>

        {/* Table */}
        <div className="bt-table-wrap">
          <table className="bt-table">
            <thead>
              <tr>
                <th>Payment Name</th>
                <th>Account Number</th>
                <th>Payment Date</th>
                <th style={{ width: '80px' }}>Payment Amount</th>
                <th style={{ width: '80px' }}>Balance Amount</th>
                <th>Reconciled</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan="7" className="bt-td-empty">No records found.</td></tr>
              ) : payments.map(p => (
                <tr key={p.id}>
                  <td>
                    <button className="bt-link" onClick={() => fetchLines(p.id, p.name)}>
                      {p.name}
                    </button>
                  </td>
                  <td>{p.account_no}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.payment_date)}</td>
                  <td>{p.payment_amount}</td>
                  <td>{p.balance_amount}</td>
                  <td>{p.reconciled}</td>
                  <td>
                    <button className="bt-btn-sm" onClick={() => handleEdit(p)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lines sub-modal */}
      {linesOpen && (
        <div className="bt-sub-overlay" onClick={() => setLinesOpen(false)}>
          <div className="bt-submodal" onClick={e => e.stopPropagation()}>
            <div className="bt-submodal-hdr">
              <span className="bt-submodal-title">Payment Lines: {linesTitle}</span>
              <button className="bt-fm-close" onClick={() => setLinesOpen(false)}>×</button>
            </div>
            <div className="bt-submodal-body">
              <table className="bt-table">
                <thead>
                  <tr>
                    <th>Payment Type</th>
                    <th>Detail</th>
                    <th>Name</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr><td colSpan="4" className="bt-td-empty">No lines found.</td></tr>
                  ) : lines.map(l => (
                    <tr key={l.id}>
                      <td>{l.payment_type_name}</td>
                      <td>{l.detail}</td>
                      <td>{l.name}</td>
                      <td>{l.payment_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
